import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { actsInPlace } from '@/lib/yourDesk';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { withCue } from '@/lib/spotlightCue';
import { useNextMove } from '@/hooks/useNextMove';
import { deskMoveCopy, isCommitmentMove, NEXT_MOVE_LABEL, NOT_NOW, type DeskMove } from '@/lib/deskNextMove';
import { recordNextMoveEvent } from '@/lib/nextMoveEvents';
import DeskMark from '@/components/desk/DeskMark';

import { deskReplyCompanyId, moveDestination } from '@/lib/deskReply';
import DeskInlineReply from '@/components/desk/DeskInlineReply';

type Surface = 'ribbon' | 'my_day';

/** One Your Next Move™ line, used by the ribbon (after Desk Setup) and the Your Desk card. */
export default function NextMoveStrip({ surface, move: given, className, onAsk, onNext, nextDisabled, pulse }: {
  /** Urgent move just arrived: a short warm heartbeat on the action button only. */
  pulse?: boolean;
  surface: Surface; move?: DeskMove | null; className?: string;
  /** Your Desk answers in place instead of opening the side panel. */
  onAsk?: (move: DeskMove | null) => void;
  /** Today's deck: adds Next beside Done. Done clears the card; the next one slides in. */
  onNext?: () => void;
  /** Locked while the desk is briefing the active card so decks never race streams. */
  nextDisabled?: boolean;
}) {
  const { move: fetched, isLoading, dismiss, mute, recordClick } = useNextMove(given === undefined);
  const move = given === undefined ? fetched : given;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const shown = useRef<string | null>(null);
  const isMove = move?.kind === 'move';
  const [replyOpen, setReplyOpen] = useState(false);
  const [going, setGoing] = useState(false);
  const replyCompanyId = deskReplyCompanyId(move);
  useEffect(() => { setReplyOpen(false); }, [replyCompanyId]);

  useEffect(() => {
    if (!move || move.kind !== 'move') return;
    const id = `${move.key}:${surface}`;
    if (shown.current === id) return;
    shown.current = id;
    void recordNextMoveEvent(move.key, 'shown', surface, move.priority);
  }, [move, surface]);

  if (isLoading && given === undefined) return null;
  const copy = deskMoveCopy(move);
  const dest = moveDestination(move);
  const to = dest && move && move.kind !== 'caught_up' ? (move.cue ? withCue(dest, move.cue) : dest) : null;
  const label = move?.kind === 'setup' ? 'Desk Setup' : NEXT_MOVE_LABEL;

  /* The click is stored before navigation so leaving the page can never cancel it.
     Modified clicks (new tab) keep the browser's default and record in the background. */
  const go = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!isMove || !to) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) { void recordClick(move.key, surface); return; }
    e.preventDefault();
    // A move whose destination is the page already open answers in place; a link to here would do nothing.
    if (onAsk && actsInPlace(to, pathname)) { void recordClick(move.key, surface); onAsk(move); return; }
    setGoing(true);
    void recordClick(move.key, surface).finally(() => { setGoing(false); navigate(to); });
  };

  if (surface === 'my_day') return <section className={cn('shrink-0 border-b border-border pb-5', className)} aria-label="Today’s priority">
    <p className="mb-2 text-xs font-medium text-brand-text">{isMove ? 'Today’s priority' : 'On your desk'}</p>
    <h2 className="max-w-2xl font-display text-2xl leading-tight sm:text-3xl">{copy.line}</h2>
    {copy.evidence && <p className="mt-2 text-sm text-muted-foreground">{copy.evidence}</p>}
    <div className="mt-4 flex flex-wrap items-center gap-2">
      {to && copy.button && (replyCompanyId ? <Button size="sm" variant="guide" className="rounded-full px-4" disabled={replyOpen}
        onClick={() => { setReplyOpen(true); if (isMove) void recordClick(move.key, surface); }}>Reply here<ArrowRight className="ml-2 h-3.5 w-3.5" /></Button> :
        <Button asChild size="sm" variant="guide" className="rounded-full px-4"><Link to={to} aria-busy={going} onClick={go}>{copy.button}<ArrowRight className="ml-2 h-3.5 w-3.5" /></Link></Button>)}
      {isMove && onNext && <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={replyOpen || nextDisabled}
        onClick={onNext}>Next<ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>}
      {isMove && <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={dismiss.isPending || replyOpen}
        title={isCommitmentMove(move.key) ? 'Mark this promise kept' : 'Hide this until tomorrow — handled it already, or not now'}
        onClick={() => dismiss.mutate({ ruleKey: move.key, surface, memoryId: typeof move.vars?.memory_id === 'string' ? move.vars.memory_id : undefined })}>{isCommitmentMove(move.key) ? NOT_NOW : 'Later'}</Button>}
      {isMove && !isCommitmentMove(move.key) && <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={mute.isPending || replyOpen}
        onClick={() => mute.mutate({ ruleKey: move.key }, {
          onSuccess: (r) => toast.success(r === 'muted' ? "Got it — we won't suggest this again." : "Got it — we'll hold off on this for a while."),
          onError: () => toast.error("Couldn't save that — try again."),
        })}>Not interested</Button>}
    </div>
    {replyOpen && replyCompanyId && <DeskInlineReply key={replyCompanyId} companyId={replyCompanyId} onClose={() => setReplyOpen(false)} onSent={() => setReplyOpen(false)} />}
  </section>;

  // Ribbon: one job — the priority and its action. Tapping the line opens Your Desk; the assistant lives in the header.
  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <Link to="/desk" aria-label={`${label}: ${copy.line} — open Your Desk`} className="flex min-w-0 flex-1 items-start gap-3 text-left">
        <DeskMark className="mt-0.5 h-5 w-8 shrink-0" />
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-brand-text">{label}</p>
          <p className="truncate text-sm leading-snug">{copy.line}</p>
          {copy.evidence && (
            <p className="truncate text-xs text-muted-foreground">{copy.evidence}</p>
          )}
        </div>
      </Link>

      {isMove && (
        <Button size="sm" variant="ghost" className="shrink-0 text-muted-foreground" disabled={dismiss.isPending}
          title={isCommitmentMove(move.key) ? 'Mark this promise kept' : 'Hide this until tomorrow — handled it already, or not now'}
          onClick={() => dismiss.mutate({ ruleKey: move.key, surface, memoryId: typeof move.vars?.memory_id === 'string' ? move.vars.memory_id : undefined })}>
          {isCommitmentMove(move.key) ? NOT_NOW : 'Later'}
        </Button>
      )}
      {to && copy.button && (
        <Button asChild size="sm" variant="guide" className={cn('shrink-0', pulse && 'cue-spotlight')}>
          <Link to={to} aria-busy={going} onClick={go}>
            {copy.button}<ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden />
          </Link>
        </Button>
      )}
    </div>
  );
}
